/**
 * Select Orchestrator
 *
 * Coordinates initialization of select enhancement functions in proper sequence.
 * Ensures states populate before custom UI is created.
 *
 * Features:
 * - Sequential initialization (states → custom-select)
 * - Error handling with partial cleanup
 * - Centralized cleanup/destroy management
 * - Passes individual configs to sub-functions
 *
 * Initialization Order:
 *   1. states - Populates native <select> with US states
 *   2. custom-select - Creates custom UI over native select
 *
 * This order is critical: custom-select needs to read the populated
 * options from the native select that states creates.
 */
import { init as statesInit } from './functions/states.ts';
import { init as customSelectInit } from './functions/custom-select.ts';

export async function init(selectConfig: any) {
  const cleanup = { destroyFunctions: [] as Array<() => void> };

  try {
    // IMPORTANT: Load states FIRST to populate native select,
    // then load custom-select to create custom UI
    const statesResult = statesInit(selectConfig.states);
    if (statesResult?.destroy) cleanup.destroyFunctions.push(statesResult.destroy);

    const customSelectResult = customSelectInit(selectConfig['custom-select']);
    if (customSelectResult?.destroy) cleanup.destroyFunctions.push(customSelectResult.destroy);

    return {
      result: 'select initialized',
      destroy: () => {
        cleanup.destroyFunctions.forEach((destroyFn) => {
          try {
            destroyFn();
          } catch (error) {
            console.error('[select] Error during cleanup:', error);
          }
        });
        cleanup.destroyFunctions.length = 0;
      },
    };
  } catch (error) {
    console.error('[select] Initialization failed:', error);
    // Cleanup any partial initialization
    cleanup.destroyFunctions.forEach((fn) => {
      try {
        fn();
      } catch (cleanupError) {
        console.error('[select] Error during error cleanup:', cleanupError);
      }
    });
    throw error;
  }
}
